import { useState, useEffect, useMemo, type FormEvent, type ChangeEvent } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { Select } from '@/components/ui';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { ApiClientError } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import {
  useTenantProfile,
  useUpdateTenantProfile,
  useClearBirthYear,
  type TenantProfileUpdate,
} from '@/hooks/useTenantProfile';

// ============================================================================
// US States and Territories
// ============================================================================

/**
 * All 50 US states + DC + territories (56 total)
 * Sorted alphabetically by full name
 */
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'GU', name: 'Guam' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
] as const;

// ============================================================================
// Birth Year Options
// ============================================================================

/**
 * Generate birth year options (1930 to current_year - 18, descending)
 * User must be at least 18 years old
 */
function generateBirthYearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18; // Must be 18+
  const minYear = 1930;

  const options: { value: string; label: string }[] = [];
  for (let year = maxYear; year >= minYear; year--) {
    options.push({ value: year.toString(), label: year.toString() });
  }
  return options;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Household Profile Settings Component
 *
 * Allows users to optionally provide birth year and state of residence
 * for additional context about their household.
 *
 * Birth year is encrypted at rest for privacy protection.
 * NOTE: FlowIO does not provide financial, investment, tax, or legal advice.
 */
export default function FinancialPlanningSettings() {
  // WP-PLAN-1 Phase 5: read and write through the one module that owns
  // ['tenant-profile', tenantId]. This component used to fetch the profile
  // itself in a useEffect and keep it in local state, which meant saving here
  // never touched React Query's cache - so the Planning page kept serving a
  // stale birth year for the full 5-minute staleTime and the user's age was
  // silently wrong until they reloaded.
  const { profile, isLoading, tenantId } = useTenantProfile();

  // The backend rejects a non-owner with 403 "Only tenant owner can update
  // profile" (services/core/app/api/v1/tenant.py:542-548). Reflecting that here
  // so a member does not fill the form in and only find out on save.
  const user = useAuthStore((state) => state.user);
  const isOwner = user?.role === 'owner';

  // showToast: false because this component renders its own inline success and
  // error Alerts; the hook's toast would say the same thing twice.
  const updateProfile = useUpdateTenantProfile({ showToast: false });
  const clearBirthYearMutation = useClearBirthYear({ showToast: false });
  const isSaving = updateProfile.isPending || clearBirthYearMutation.isPending;

  // State
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Form state
  const [birthYear, setBirthYear] = useState<string>('');
  const [stateOfResidence, setStateOfResidence] = useState<string>('');

  // Generate options once
  const birthYearOptions = useMemo(() => generateBirthYearOptions(), []);
  const stateOptions = useMemo(() =>
    US_STATES.map(state => ({ value: state.code, label: state.name })),
    []
  );

  // Seed the editable form fields from the fetched profile.
  //
  // This no longer fetches - useTenantProfile does. It only mirrors the server
  // value into local form state so the inputs stay editable (a controlled input
  // cannot read straight from the cache without losing in-progress typing).
  // Keyed on the profile object, so a refetch after a save re-syncs the fields.
  useEffect(() => {
    if (!profile) return;
    setBirthYear(profile.birthYear?.toString() ?? '');
    setStateOfResidence(profile.stateOfResidence ?? '');
    // Auto-expand if there's existing data
    if (profile.birthYear || profile.stateOfResidence) {
      setIsExpanded(true);
    }
  }, [profile]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;

    setError(null);
    setSuccess(false);

    try {
      const updateData: TenantProfileUpdate = {};

      // Only include changed fields
      const newBirthYear = birthYear ? parseInt(birthYear, 10) : null;
      if (newBirthYear !== profile?.birthYear) {
        updateData.birthYear = newBirthYear;
      }

      const newState = stateOfResidence || null;
      if (newState !== profile?.stateOfResidence) {
        updateData.stateOfResidence = newState;
      }

      // Only call API if there are changes. mutateAsync so the existing
      // try/catch still drives the inline Alert; the hook invalidates
      // ['tenant-profile', tenantId] on success, which is what makes the
      // Planning page pick this up without a reload.
      if (Object.keys(updateData).length > 0) {
        await updateProfile.mutateAsync(updateData);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to save profile settings.');
      }
    }
  };

  const handleClearBirthYear = async () => {
    if (!tenantId || !profile?.birthYear) return;

    if (!window.confirm('Are you sure you want to clear your birth year?')) {
      return;
    }

    setError(null);

    try {
      await clearBirthYearMutation.mutateAsync();
      setBirthYear('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to clear birth year.');
      }
    }
  };

  // Check if there are unsaved changes
  const hasChanges = () => {
    const currentBirthYear = birthYear ? parseInt(birthYear, 10) : null;
    const currentState = stateOfResidence || null;
    return currentBirthYear !== profile?.birthYear || currentState !== profile?.stateOfResidence;
  };

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Household Profile"
        description="Optional context about your household"
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-controls="financial-planning-content"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        }
      />

      {isExpanded && (
        <div id="financial-planning-content">
          {success && (
            <Alert variant="success" className="mb-4">
              Settings saved successfully.
            </Alert>
          )}

          {error && (
            <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <p className="text-sm text-gray-600 mb-6">
            This optional information provides additional context for your financial data.
            FlowIO does not provide financial, investment, tax, or legal advice.
          </p>

          {!isOwner && (
            <Alert variant="info" className="mb-6">
              Only the account owner can update these details. You can see them
              here, but not change them.
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Birth Year */}
            <div>
              <Select
                label="Birth Year"
                id="birth-year"
                value={birthYear}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setBirthYear(e.target.value)}
                options={birthYearOptions}
                placeholder="Select year"
                hint="Provides age context for your financial data."
                disabled={!isOwner}
              />
              {profile?.birthYear && isOwner && (
                <button
                  type="button"
                  onClick={handleClearBirthYear}
                  disabled={isSaving}
                  className="mt-2 text-sm text-red-600 hover:text-red-700 underline focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded"
                >
                  Clear birth year
                </button>
              )}
            </div>

            {/* State of Residence */}
            <Select
              label="State of Residence"
              id="state-of-residence"
              value={stateOfResidence}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setStateOfResidence(e.target.value)}
              options={stateOptions}
              placeholder="Select state"
              hint="Provides geographic context for your financial data."
              disabled={!isOwner}
            />

            {/* Privacy Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Your birth year is encrypted at rest for privacy protection.
                    We never share this information with third parties.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button - hidden entirely for non-owners, who cannot save.
                Showing a permanently disabled button would just be a dead
                control; the notice above already explains why. */}
            {isOwner && (
            <div className="pt-4">
              <Button
                type="submit"
                disabled={!hasChanges() || isSaving}
                isLoading={isSaving}
              >
                Save changes
              </Button>
            </div>
            )}
          </form>
        </div>
      )}
    </Card>
  );
}
